import { useHandler } from "@chatbotgang/etude/react/useHandler";
import CircularProgress from "@mui/material/CircularProgress";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Switch } from "@/components/Switch";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import { interlude } from "@/interlude";

const styles = {
  popoverPaper: {
    padding: "16px",
    width: "320px",
    border: `1px solid ${theme.colors.neutral020}`,
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    py: "24px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const alertSettingLinkStyle: React.CSSProperties = {
  color: "inherit",
  textDecoration: "underline",
};

export const BillingMonthlyReportToggle: React.FC =
  function BillingMonthlyReportToggle() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const isPopoverOpen = anchorEl !== null;

    const billingSettingsQuery = interlude.billing.useGetBillingSettings();
    const notifyEmailsQuery = interlude.billing.useGetNotifyEmails();
    const updateBillingSettingsMutation =
      interlude.billing.useUpdateBillingSettings(undefined, {
        onError: () => {
          enqueueSnackbar(t("common.apiError.unexpectedError"), {
            variant: "error",
          });
        },
      });

    const isChecked = billingSettingsQuery.data?.monthlyReportEnabled ?? false;

    const isToggleDisabled =
      updateBillingSettingsMutation.isPending ||
      notifyEmailsQuery.isPending ||
      (!isChecked && notifyEmailsQuery.data?.notifyEmails.length === 0);

    const handleOpen = useHandler<React.MouseEventHandler<HTMLElement>>(
      (event) => {
        setAnchorEl(event.currentTarget);
      },
    );

    const handleClose = useHandler(() => {
      setAnchorEl(null);
    });

    const handleToggle = useHandler<
      React.ComponentProps<typeof Switch>["onChange"]
    >((_, nextEnabled) => {
      updateBillingSettingsMutation.mutate({
        monthlyReportEnabled: nextEnabled,
      });
    });

    const alertSettingLinkSlot = useMemo(
      () => (
        <Link
          to="/billing"
          search={{ tab: "alert-setting" }}
          onClick={handleClose}
          style={alertSettingLinkStyle}
        />
      ),
      [handleClose],
    );

    return (
      <>
        <Button variant="secondary" onClick={handleOpen}>
          {t("billing.monthlyReportSubscription.button")}
        </Button>
        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: styles.popoverPaper } }}
        >
          {billingSettingsQuery.isPending || notifyEmailsQuery.isPending ? (
            <Stack sx={styles.loadingContainer}>
              <CircularProgress size={24} />
            </Stack>
          ) : billingSettingsQuery.isError ? (
            <Typography variant="body">{t("billing.error.generic")}</Typography>
          ) : (
            <Stack gap="12px">
              <Typography variant="h3" fontWeight={600}>
                {t("billing.monthlyReportSubscription.popover.title")}
              </Typography>
              <Stack gap="4px">
                <Typography variant="body" color={theme.colors.neutral080}>
                  {t("billing.monthlyReportSubscription.popover.description")}
                </Typography>
                <Typography variant="note" color={theme.colors.neutral060}>
                  <Trans
                    i18nKey="billing.monthlyReportSubscription.popover.recipientHint"
                    components={{ alertLink: alertSettingLinkSlot }}
                  />
                </Typography>
              </Stack>
              {!isChecked &&
              notifyEmailsQuery.isSuccess &&
              notifyEmailsQuery.data.notifyEmails.length === 0 ? (
                <Typography variant="note" color={theme.colors.colorRed60}>
                  <Trans
                    i18nKey="billing.monthlyReportSubscription.emptyRecipients"
                    components={{ alertLink: alertSettingLinkSlot }}
                  />
                </Typography>
              ) : null}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="16px"
              >
                <Typography variant="body">
                  {t("billing.monthlyReportSubscription.popover.toggleLabel")}
                </Typography>
                <Switch
                  checked={isChecked}
                  onChange={handleToggle}
                  loading={updateBillingSettingsMutation.isPending}
                  disabled={isToggleDisabled}
                />
              </Stack>
            </Stack>
          )}
        </Popover>
      </>
    );
  };
